// import React, { useEffect, useState } from "react";
// import GenericTable from "../GenericTable/GenericTable";
// import productsList from "../../../data/products_list_success.json"; // Assuming you have a JSON file with user data
// import { CircularProgress } from "@mui/material";   

// const ProductsColumns = [
//   { id: "product_code", label: "Product Code" },
//   { id: "product_name", label: "Product Name" },
//   {
//   id: "product_price",
//   label: "Product Price",
//   render: (row) => `$ ${Number(row.product_price).toLocaleString()}`
// },
//   { id: "category", label: "Category" },
//   { id: "in_stock", label: "In Stock" },
//   { id: "last_sync_date", label: "Last Sync Date" }
// ];

// function ProductsList() {
//   const [loading, setLoading] = useState(true);   
  
//     useEffect(() => {
//       // simulate loading delay (like API call)
//       const timer = setTimeout(() => {
//         setLoading(false);
//       }, 150); // 0.5 sec spinner
  
//       return () => clearTimeout(timer);
//     }, []);
  

//     if (loading) {
//       return (
//         <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
//           <CircularProgress />
//         </div>
//       );
//     }

//   return (
//  <GenericTable
//       title="Products List"
//       columns={ProductsColumns}
//       rows={productsList.data}
//       showView={false}
//       showEdit={false}
//       showDelete={false}
//       dropdownFilters={["product_name"]}
//       rangeFilters={["product_price"]}
//     />
//   );
// }

// export default ProductsList;


import React, { useEffect, useState, useCallback } from "react";
import GenericTable from "../GenericTable/GenericTable";
import { CircularProgress, Box, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../../config";
 
// Helper to safely show N/A
const formatValue = (value) =>
  value === null || value === undefined || value === "" ? "N/A" : value;
 
// UPDATED: Columns definition
const ProductsColumns = [
  {
    id: "product_code",
    label: "Product Code",
    render: (row) => formatValue(row.product_code),
  },
  {
    id: "product_name",
    label: "Product Name",
    render: (row) => formatValue(row.product_name),
  },
  {
    id: "product_price",
    label: "Product Price",
    render: (row) =>
      row.product_price === null || row.product_price === undefined
        ? "N/A"
        : `$${Number(row.product_price).toLocaleString()}`,
  },
  {
    id: "category",
    label: "Category",
    render: (row) => formatValue(row.category),
  },
  {
    id: "inventory_quantity",
    label: "In Stock",
    render: (row) => formatValue(row.inventory_quantity),
  },
  {
    id: "last_sync_date",
    label: "Last Sync Date",
    render: (row) =>
      row.last_sync_date
        ? new Date(row.last_sync_date).toLocaleDateString()
        : "N/A",
  },
];
 
function ProductsList() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // ✅ FILTERS STATE
  const [search, setSearch] = useState("");
  const [priceRange, setPriceRange] = useState([0, 999999]); // ✅ REMOVED: productName (redundant with search)

  // ✅ EXPORT STATE
  const [exportOpen, setExportOpen] = useState(false);
  const [exportType, setExportType] = useState("csv");

  // ✅ PAGINATION STATE
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // ⭐ Redirect Function
  const handleView = (row) => {
    navigate(`/product/details/${row.store_pos_product_id}`);
  };

  // ✅ UNIVERSAL FETCH: Works with ANY filters (search, min_price, max_price, or future filters)
  // This function is called by:
  // 1. Search button click (with all current filter values)
  // 2. Pagination changes (page/rowsPerPage) - keeps current filters
  const fetchProducts = useCallback(async (pageNum = page, rowSize = rowsPerPage, filters = {}) => {
    setLoading(true);
    try {
      // ✅ Merge provided filters with defaults
      const queryParams = {
        page: pageNum,
        page_size: rowSize,
        search: filters.search !== undefined ? filters.search : search,
        min_price: filters.min_price !== undefined ? filters.min_price : priceRange[0],
        max_price: filters.max_price !== undefined ? filters.max_price : priceRange[1],
      };

      // Remove undefined/empty values
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] === undefined || queryParams[key] === "") {
          delete queryParams[key];
        }
      });

      const res = await axios.get(`${config.API_BASE_URL_V1}/admin/product`, {
        params: queryParams,
      });

      const rawData = res.data?.data || [];

      // Apply N/A formatting to each row
      const cleanData = rawData.map((item) => ({
        product_code: formatValue(item.product_code),
        store_pos_product_id: formatValue(item.store_pos_product_id),
        product_name: formatValue(item.product_name),
        product_price:
          item.product_price === null || item.product_price === undefined
            ? "N/A"
            : item.product_price,
        category: formatValue(item.category),
        inventory_quantity: formatValue(item.inventory_quantity),
        last_sync_date: item.last_sync_date || null,
      }));

      setRows(cleanData);
      setTotalCount(res.data?.pagination?.total_records || 0);
    } catch (error) {
      console.error("Product API Error:", error);
      setRows([]);
    }
    setLoading(false);
  }, [page, rowsPerPage, search, priceRange]);

  // ✅ UNIVERSAL SEARCH: Called when user clicks Search button
  // Passes ALL current filter values (search, min_price, max_price, and any future filters)
  const handleSearch = () => {
    setPage(1); // Reset to first page
    // Call fetchProducts with page 1 and all current filter values
    fetchProducts(1, rowsPerPage, {
      search: search,
      min_price: priceRange[0],
      max_price: priceRange[1],
      // Future filters can be added here (e.g., category, status, etc.)
    });
  };

  // ✅ CLEAR FILTERS: Reset all filters and search, then fetch fresh data
  const handleClearFilters = () => {
    setSearch("");
    setPriceRange([0, 999999]);
    setPage(1);
    // ✅ Fetch fresh data with no filters
    fetchProducts(1, rowsPerPage, {
      search: "",
      min_price: 0,
      max_price: 999999,
    });
  };

  // ✅ AUTO-FETCH: When page or rowsPerPage changes (keeps current filters)
  useEffect(() => {
    // Only fetch if page or rowsPerPage changed (not on initial mount or filter changes)
    // This preserves the current filters while allowing pagination
    fetchProducts(page, rowsPerPage, {
      search: search,
      min_price: priceRange[0],
      max_price: priceRange[1],
    });
  }, [page, rowsPerPage]);
 
  // ✅ EXPORT: Get ALL filtered results (ignores pagination)
  const handleExportData = async () => {
    try {
      setLoading(true);
      let allData = [];
      let currentPage = 1; // ✅ Backend uses 1-indexed pagination (page 1, 2, 3, ...)
      let hasMore = true;

      // ✅ Log the filters being used
      console.log(`🔍 Export Filters:`, {
        search: search || "NONE",
        min_price: priceRange[0],
        max_price: priceRange[1],
      });

      // ✅ Fetch ALL pages until no more data
      while (hasMore) {
        console.log(`🔄 Fetching page ${currentPage} with page_size=200...`);

        const res = await axios.get(`${config.API_BASE_URL_V1}/admin/product`, {
          params: {
            page: currentPage,
            page_size: 200, // ✅ Backend max limit is 200
            search: search || undefined,
            min_price: priceRange[0],
            max_price: priceRange[1],
          },
        });

        const pageData = res.data?.data || [];
        const totalRecords = res.data?.pagination?.total_records || 0;

        console.log(`📄 Export Page ${currentPage}: Fetched ${pageData.length} rows (Total in DB: ${totalRecords})`);
        console.log(`📋 Response:`, res.data);

        if (pageData.length === 0) {
          console.log(`✅ No more data. Total rows fetched: ${allData.length}`);
          hasMore = false;
        } else {
          allData = [...allData, ...pageData];
          currentPage++;
        }
      }

      console.log(`📊 Total data for export: ${allData.length} rows`);

      if (!allData.length) {
        setLoading(false);
        return alert("No data found!");
      }

      if (exportType === "csv") {
        const headers = Object.keys(allData[0]);
        const csvRows = [headers.join(",")];

        allData.forEach((row) => {
          csvRows.push(headers.map((h) => JSON.stringify(row[h] ?? "N/A")).join(","));
        });

        console.log(`📥 CSV created with ${csvRows.length - 1} data rows (plus 1 header row)`);

        const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `products_export_${new Date().toISOString()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      setExportOpen(false);
      setLoading(false);
    } catch (error) {
      console.error("Export Error:", error);
      alert("Failed to export data");
      setLoading(false);
    }
  };
 
  return (
    <div>
      {/* ✅ FILTER BAR - WHITE BACKGROUND */}
      <Box sx={{ mb: 0, p: 2, backgroundColor: "#ffffff", borderRadius: "8px 8px 0 0", display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", border: "1px solid #e0e0e0", borderBottom: "none" }}>
        <TextField
          label="Search"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products..."
          sx={{ minWidth: 200 }}
        />

        <TextField
          label="Min Price"
          type="number"
          size="small"
          value={priceRange[0] === 0 ? "" : priceRange[0]}
          onChange={(e) => {
            const val = e.target.value === "" ? 0 : Number(e.target.value);
            setPriceRange([val, priceRange[1]]);
          }}
          placeholder="0"
          sx={{ minWidth: 120 }}
        />

        <TextField
          label="Max Price"
          type="number"
          size="small"
          value={priceRange[1] === 99999 ? "" : priceRange[1]}
          onChange={(e) => {
            const val = e.target.value === "" ? 99999 : Number(e.target.value);
            setPriceRange([priceRange[0], val]);
          }}
          placeholder="99999"
          sx={{ minWidth: 120 }}
        />

        <Button
          variant="contained"
          onClick={handleSearch}
          sx={{ height: "40px", backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#1565c0" } }}
        >
          Search
        </Button>

        <Button
          variant="outlined"
          onClick={handleClearFilters}
          sx={{ height: "40px", borderColor: "#1976d2", color: "#1976d2", "&:hover": { backgroundColor: "#f5f5f5" } }}
        >
          Clear Filters
        </Button>

        <Button
          variant="contained"
          onClick={() => setExportOpen(true)}
          sx={{ height: "40px", backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#1565c0" } }}
        >
          Export
        </Button>
      </Box>

      {/* ✅ EXPORT DIALOG */}
      <Dialog open={exportOpen} onClose={() => setExportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle textAlign="center">Export Data</DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1, pb: 2 }}>
          <RadioGroup value={exportType} onChange={(e) => setExportType(e.target.value)}>
            <FormControlLabel value="csv" control={<Radio />} label="CSV" />
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 2 }}>
          <Button variant="outlined" onClick={() => setExportOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleExportData} autoFocus>Download</Button>
        </DialogActions>
      </Dialog>

      {/* ✅ LOADING INDICATOR */}
      {loading && (
        <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
          <CircularProgress />
        </div>
      )}

      {/* ✅ TABLE WITH SERVER-SIDE PAGINATION */}
      {!loading && (
        <GenericTable
          title="" // ✅ REMOVED: Products List heading
          columns={ProductsColumns}
          rows={rows}
          showView={true}
          showEdit={false}
          showDelete={false}
          hideExport={true} // ✅ Hide export button - using custom export in filter bar
          // ✅ SERVER-SIDE PAGINATION ENABLED
          isServerSide={true}
          currentPage={page - 1} // Convert 1-indexed to 0-indexed for MUI
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={(_, newPage) => {
            setPage(newPage + 1); // Convert 0-indexed back to 1-indexed
          }}
          onRowsPerPageChange={(event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            setRowsPerPage(newRowsPerPage);
            setPage(1); // Reset to first page
          }}
          onView={(row) => handleView(row)}
        />
      )}
    </div>
  );
}
 
export default ProductsList;